import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Program } from '@/entities/program';
import { AppConstructor } from '../components/constructor/Constructor';
import { ImageShape } from '../lib/types';
import { axiosAPI } from './axios-instance';
import { ApiResponse } from './lib/types';
import { transformConstructorFromApi } from './lib/utils/transform-constructor';

export type ApiProgramRequest = Partial<{
    participation_formats: number[];
    directions: number[];
    day: number;
    days: number[];
    q: string;
    is_free: number;
}>;

type Params = {
    params?: ApiProgramRequest;
    signal?: AbortSignal;
};

export type AppProgramResponse = ApiResponse<{
    totalCount: number;
    items: {
        id: number;
        slug: string;
        time: string;
        prefix: string;
        name: string;
        place: number;
        image: ImageShape;
        participationFormats: number[];
        companies: ImageShape[];
        description: string;
        content: AppConstructor;
        directions: number[];
        day: number;
    }[];
}>;

export type ApiProgramResponse = ApiResponse<{
    items: Program[];
    totalCount: number;
}>;

const transformApiResponse = (response: ApiProgramResponse): AppProgramResponse => {
    return {
        ...response,
        data: {
            totalCount: response.data?.totalCount || 0,
            items: (response.data?.items ?? []).map((item) => ({
                id: item.id,
                slug: item.slug || '',
                name: item.name || '',
                content: transformConstructorFromApi(item.content || []),
                description: item.description || '',
                prefix: item.prefix || '',
                time: item.time,
                image: item.image || {},
                place: item.place,
                directions: item.directions,
                participationFormats: item.participation_formats || [],
                day: item.day,
                companies: item.companies || [],
            })),
        },
    };
};

export const getProgram = ({ params = {}, signal }: Params = {}): Promise<AppProgramResponse> =>
    axiosAPI.get<ApiProgramResponse>('/v1/programs', { params, signal }).then((res) => transformApiResponse(res.data));

export const useProgramQuery = (data: Params, initialData?: AppProgramResponse) =>
    useQuery({
        queryKey: ['program', data],
        queryFn: () => getProgram(data),
        initialData,
        placeholderData: keepPreviousData,
    });
